import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { returnMode } from './difficultyReducer'
import { IQuestion } from '../types'
import { ReducerProps } from '../../../types'
import {
  defaultQuizHighscores,
  QUIZ_DEFAULT_TIME,
  QUIZ_POINTS_PER_CORRECT,
  QUIZ_QUESTION_COUNT,
} from '../utils/scores'

function shuffleArray<T>(array: T[]): T[] {
  const newArray = [...array]
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[newArray[i], newArray[j]] = [newArray[j], newArray[i]]
  }
  return newArray
}

const initialState: ReducerProps['questions'] = {
  questionsRedux: [],
  status: 'ready',
  index: 0,
  currentQuestion: {},
  answer: null,
  points: 0,
  secondsRemaining: QUIZ_DEFAULT_TIME,
  finalSeconds: 0,
  highscores: defaultQuizHighscores,
}

export const getQuestions = createAsyncThunk(
  'questions/getQuestions',
  async (difficulty: string): Promise<IQuestion[]> => {
    try {
      const resp = await fetch(
        `https://the-trivia-api.com/v2/questions?limit=${QUIZ_QUESTION_COUNT}&difficulties=${difficulty}`
      )
      // Cast response to the expected IQuestion[] shape
      const data = (await resp.json()) as IQuestion[]
      return data
    } catch (error) {
      console.error(error)
      return []
    }
  }
)

const questionsSlice = createSlice({
  name: 'questions',
  initialState,
  reducers: {
    addQuiz: (
      _state,
      { payload }: PayloadAction<ReducerProps['questions']>
    ) => {
      return payload
    },
    resetTimer: (state) => {
      state.secondsRemaining = QUIZ_DEFAULT_TIME
    },
    lessSeconds: (state) => {
      state.secondsRemaining -= 1
    },
    finalSeconds: (state) => {
      state.finalSeconds = QUIZ_DEFAULT_TIME - state.secondsRemaining
    },
    newAnswer: (state, { payload }: PayloadAction<string>) => {
      state.answer = payload
      if (typeof state.currentQuestion.correctAnswer === 'string')
        state.points =
          payload === state.currentQuestion.correctAnswer
            ? state.points + QUIZ_POINTS_PER_CORRECT
            : state.points
    },
    nextQuestion: (state) => {
      const temp: IQuestion | undefined = state.questionsRedux[state.index + 1]
      if (!temp) return state
      const newArray = {
        id: temp.id,
        correctAnswer: temp.correctAnswer,
        question: temp.question.text,
        options: shuffleArray([...temp.incorrectAnswers, temp.correctAnswer]),
      }
      state.index += 1
      state.currentQuestion = newArray
      state.answer = null
    },
    gameFinished: (state) => {
      state.highscores[returnMode() as keyof typeof state.highscores].score =
        state.points >
        state.highscores[
          returnMode() as unknown as keyof typeof state.highscores
        ].score
          ? state.points
          : state.highscores[
              returnMode() as unknown as keyof typeof state.highscores
            ].score
      state.highscores[returnMode() as keyof typeof state.highscores].time =
        state.finalSeconds <
        state.highscores[
          returnMode() as unknown as keyof typeof state.highscores
        ].time
          ? state.finalSeconds
          : state.highscores[
              returnMode() as unknown as keyof typeof state.highscores
            ].time
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getQuestions.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(
        getQuestions.fulfilled,
        (state, { payload }: PayloadAction<IQuestion[]>) => {
          if (!payload || payload.length === 0) {
            state.status = 'ready'
            state.questionsRedux = []
            state.currentQuestion = {}
            state.index = 0
            state.points = 0
            state.answer = null
            return
          }
          const temp = payload[0]
          const newArray = {
            id: temp.id,
            correctAnswer: temp.correctAnswer,
            question: temp.question.text,
            options: shuffleArray([
              ...temp.incorrectAnswers,
              temp.correctAnswer,
            ]),
          }
          state.status = 'ready'
          state.questionsRedux = payload
          state.currentQuestion = newArray
          state.index = 0
          state.points = 0
          state.answer = null
        }
      )
      .addCase(getQuestions.rejected, (state) => {
        state.status = 'error'
      })
  },
})

export const {
  newAnswer,
  nextQuestion,
  gameFinished,
  finalSeconds,
  lessSeconds,
  resetTimer,
} = questionsSlice.actions
export default questionsSlice.reducer
